// @ts-check

// Vendor imports
const React = require('react')
// Shared imports
const { ExtendableError } = require('shared/error-utils')
// Client lib imports
const airbrake = require('client/lib/airbrake')
// Client V2 common imports
const useClientEffect = require('client-v2/common/hooks/client-effect')
const tag = require('client-v2/common/utils/hyperscript')

const context = React.createContext({
  /** @type {Set<string> | null} */
  iconIds: null
})

/**
 * React Component that provides the Icons context.
 * @param {Object} props
 * @param {React.ElementType} props.children
 */
function IconsManager(props) {
  const iconIds = useIconIds()
  return (
    tag(context.Provider, {
      value: { iconIds }
    },
      props.children
    )
  )
}

if (__DEV__) {
  // Prop-checking imports
  const PropTypes = require('prop-types')

  IconsManager.propTypes = {
    children: PropTypes.node
  }
}

/**
 * React hook gather SVG symbol IDs from `<svg id="icons">`
 * @returns {Set<string> | null} icon IDs
 */
function useIconIds() {
  const [ iconIds, setIconIds ] = React.useState(null)
  const gatherIconIds = React.useCallback(
    () => {
      const iconsEl = document.getElementById('icons')
      if (!iconsEl) {
        return false
      }
      const iconIds = new Set()
      for (let symbolEl of iconsEl.getElementsByTagName('symbol')) {
        iconIds.add(symbolEl.id)
      }
      setIconIds(iconIds)
      return true
    },
    [ setIconIds ]
  )
  useClientEffect(
    () => {
      if (gatherIconIds()) {
        return
      }
      // Wait for `<svg id="icons">` to be populated on `<body>`
      const iconsObserver = new MutationObserver(() => {
        if (gatherIconIds()) {
          iconsObserver.disconnect()
        }
      })
      iconsObserver.observe(document.body, { childList: true })
    },
    [ gatherIconIds ]
  )
  return iconIds
}

/**
 * React hook to verify that `iconId` is a usable icon
 * @param {string} iconId
 * @returns {void}
 */
function useIconWarning(iconId) {
  const { iconIds } = React.useContext(context)
  React.useEffect(
    () => {
      if (iconIds && !iconIds.has(iconId)) {
        airbrake.notify(new IconMissingWarning(iconId))
      }
    },
    [ iconIds, iconId ]
  )
}

class IconMissingWarning extends ExtendableError {
  /**
   * @param {string} iconId
   */
  constructor(iconId) {
    super(`Usage of missing icon: ${iconId}`)
    this.name = 'IconMissingWarning' // Preserve name after minifying
  }
}

module.exports = {
  // Components
  IconsManager,
  // Hooks
  useIconIds,
  useIconWarning
}
